import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CalendarDays, CarFront, ChevronDown, HelpCircle } from "lucide-react";
import { addDays, format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import InquirySheet from "./InquirySheet";
import VehiclePickerSheet, { imageForVehicle, type VehiclePickerOption } from "./VehiclePickerSheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { writeInquiryPrefill } from "@/lib/inquiryPrefill";
import { trackEvent } from "@/lib/analytics";
import { useVehicleList } from "@/hooks/useVehicleList";
import { useBookingDock } from "./BookingDock";

interface DisplayOption {
  id: string;
  dbName: string;
  displayName: string;
  displaySubtitle: string;
  displayLabel: string;
  vehicleType: "kuehl" | "transporter" | "kleinbus";
  fullLabel: string;
  imageUrl?: string | null;
  specs?: { label: string; value: string }[];
  isAdvice?: boolean;
}

const DISPLAY_OVERRIDES: Record<string, { name: string; subtitle: string }> = {
  "Mercedes Vito Kühlkastenwagen": {
    name: "Frische-Kasten / Kühlkastenwagen",
    subtitle: "Mercedes Vito",
  },
  "Kühltransporter Koffer 3,5t": {
    name: "Kühltransporter Koffer 3,5t",
    subtitle: "Mercedes Sprinter mit Kühlkoffer",
  },
};

const toIso = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

interface HeroBookingBarProps {
  redirectToHome?: boolean;
  /** "hero" = volle Leiste im Hero, "docked" = kompakte Leiste im Header-Dock. */
  variant?: "hero" | "docked";
}

const HeroBookingBar = ({ redirectToHome = false, variant = "hero" }: HeroBookingBarProps) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === "en";
  const dateLocale = isEnglish ? enUS : de;
  const dateFormat = isEnglish ? "EEE, MM/dd/yyyy" : "EEE, dd.MM.yyyy";
  const isDocked = variant === "docked";
  const { registerHero, unregisterHero, selection, setSelection } = useBookingDock();
  const rootRef = useRef<HTMLDivElement>(null);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const minIso = toIso(today);

  // Hero-Variante meldet dem Dock, ob sie noch sichtbar ist.
  useEffect(() => {
    if (isDocked) return;
    registerHero(false);
    return () => unregisterHero();
  }, [isDocked, registerHero, unregisterHero]);

  useEffect(() => {
    if (isDocked) return;
    const node = rootRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) registerHero(!entry.isIntersecting);
      },
      { rootMargin: "-96px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isDocked, registerHero]);

  const vehicles = useVehicleList();

  const options: DisplayOption[] = useMemo(() => {
    return vehicles
      .filter((v) => v.vehicle_type === "kuehl")
      .map((v) => {
        const override = DISPLAY_OVERRIDES[v.name];
        const displayName = override?.name ?? v.name;
        const displaySub = override?.subtitle ?? v.subtitle;
        return {
          id: v.id,
          dbName: v.name,
          displayName,
          displaySubtitle: displaySub,
          displayLabel: `${displayName} — ${displaySub}`,
          vehicleType: "kuehl" as const,
          fullLabel: `${displayName} — ${displaySub}`,
          imageUrl: v.image_url,
          specs: v.specs,
        };
      });
  }, [vehicles]);

  const { vehicleId, pickup, returnD } = selection;
  const setVehicleId = (v: string) => setSelection({ vehicleId: v });
  const setPickup = (v: string) => setSelection({ pickup: v });
  const setReturnD = (v: string) => setSelection({ returnD: v });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetVehicle, setSheetVehicle] = useState<DisplayOption | null>(null);
  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openSheetAfterPick, setOpenSheetAfterPick] = useState(false);



  const minReturn = pickup || minIso;

  const openInquirySheetWithVehicle = (v: DisplayOption) => {
    const finalReturn = pickup
      ? (returnD || toIso(addDays(new Date(pickup + "T00:00:00"), 1)))
      : "";
    if (pickup) {
      writeInquiryPrefill({
        pickupDate: pickup,
        returnDate: finalReturn,
        source: "hero_bookingbar",
      });
    }
    trackEvent({
      eventType: "hero_bookingbar_submit",
      eventData: {
        vehicle_type: v.vehicleType,
        vehicle_id: v.id,
        vehicle_name: v.dbName,
        pickup_date: pickup || null,
        return_date: finalReturn || null,
        missing_pickup: !pickup,
      },
    });
    setSheetVehicle(v);
    setSheetOpen(true);
  };

  const handleSubmit = () => {
    // Nie disabled: fehlende Angaben werden im Sheet nachgefragt.
    if (!vehicleId) {
      trackEvent({
        eventType: "hero_bookingbar_open_picker_from_cta",
        eventData: { reason: "vehicle_missing", has_pickup: !!pickup },
      });
      setOpenSheetAfterPick(true);
      setVehiclePickerOpen(true);
      return;
    }
    const v = options.find((o) => o.id === vehicleId) ?? (sheetVehicle?.id === vehicleId ? sheetVehicle : null);
    if (!v) return;
    openInquirySheetWithVehicle(v);
  };

  const handleHeroCta = () => {
    if (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches) {
      setDropdownOpen(true);
      return;
    }
    handleSubmit();
  };

  const handleVehiclePicked = (option: VehiclePickerOption, openAfterPick = false) => {
    const o: DisplayOption = option.isAdvice
      ? {
          ...option,
          displayLabel: option.displayName,
          fullLabel: option.displayName,
          vehicleType: "kuehl",
          isAdvice: true,
        }
      : options.find((vehicle) => vehicle.id === option.id) ?? {
          ...option,
          displayLabel: `${option.displayName} — ${option.displaySubtitle}`,
          fullLabel: `${option.displayName} — ${option.displaySubtitle}`,
          vehicleType: "kuehl",
        };
    setVehicleId(o.id);
    setSheetVehicle(o);
    setVehiclePickerOpen(false);
    if (openAfterPick || openSheetAfterPick) {
      setOpenSheetAfterPick(false);
      openInquirySheetWithVehicle(o);
    }
  };

  // Preistabelle kann die Anfrage direkt mit dem gewählten Fahrzeug öffnen.
  useEffect(() => {
    if (isDocked) return;
    const handler = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (typeof id !== "string") return;
      const v = options.find((o) => o.id === id);
      if (!v) return;
      setVehicleId(v.id);
      openInquirySheetWithVehicle(v);
    };
    window.addEventListener("open-inquiry-for-vehicle", handler);
    return () => window.removeEventListener("open-inquiry-for-vehicle", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDocked, options, pickup, returnD]);

  const labelCls =
    "hidden lg:block text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground mb-1.5";
  const fieldHeight = isDocked ? "h-9" : "h-12 lg:h-[52px]";
  const fieldBase =
    `${fieldHeight} w-full bg-card border border-border rounded-[10px] px-3 text-sm text-foreground ` +
    "focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors";



  const parseIso = (v: string) => (v ? new Date(`${v}T00:00:00`) : undefined);

  const DateField = ({
    id,
    value,
    onSelect,
    minDate,
    placeholder,
    placeholderMobile,
    ariaLabel,
  }: {
    id: string;
    value: string;
    onSelect: (iso: string) => void;
    minDate: Date;
    placeholder: string;
    placeholderMobile?: string;
    ariaLabel: string;
  }) => {
    const selected = parseIso(value);
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            className={`${fieldBase} pl-9 pr-3 relative text-left flex items-center cursor-pointer`}
            aria-label={ariaLabel}
          >
            <CalendarDays
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent"
              strokeWidth={2}
              aria-hidden
            />
            {selected ? (
              <span className="font-semibold text-foreground truncate">
                {format(selected, dateFormat, { locale: dateLocale })}
              </span>
            ) : (
              <span className="text-muted-foreground truncate">
                <span className="lg:hidden">{placeholderMobile ?? placeholder}</span>
                <span className="hidden lg:inline">{placeholder}</span>
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[60]" align="start">
          <Calendar
            mode="single"
            locale={dateLocale}
            weekStartsOn={1}
            selected={selected}
            defaultMonth={selected ?? minDate}
            disabled={(d) => d < minDate}
            onSelect={(d) => onSelect(d ? toIso(d) : "")}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  };

  const selectedOption = options.find((o) => o.id === vehicleId) ?? (sheetVehicle?.id === vehicleId ? sheetVehicle : undefined);

  const vehicleFieldInner = (
    <>
      <CarFront
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent"
        strokeWidth={2}
        aria-hidden
      />
      {options.length === 0 ? (
        <span className="text-muted-foreground truncate">{t("heroBookingBar.loadingVehicles")}</span>
      ) : !selectedOption ? (
        <span className="text-muted-foreground truncate">{t("heroBookingBar.selectVehiclePlaceholder")}</span>
      ) : isDocked ? (
        <span className="font-semibold text-foreground truncate">{selectedOption.displayName}</span>
      ) : (
        <span className="flex items-center gap-2.5 min-w-0">
          <span className="hidden lg:flex shrink-0 w-11 h-8 rounded-md bg-secondary overflow-hidden items-center justify-center">
            <img
              src={imageForVehicle(selectedOption.dbName, selectedOption.imageUrl)}
              alt=""
              aria-hidden
              className="w-full h-full object-contain"
              loading="lazy"
              decoding="async"
            />
          </span>
          <span className="flex flex-col leading-tight min-w-0">
            <span className="font-semibold text-foreground truncate">{selectedOption.displayName}</span>
            <span className="text-[11px] text-muted-foreground truncate">{selectedOption.displaySubtitle}</span>
          </span>
        </span>
      )}
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
        strokeWidth={2}
        aria-hidden
      />
    </>
  );

  const vehicleFieldCls = `${fieldBase} pl-9 pr-9 relative text-left flex items-center cursor-pointer disabled:cursor-not-allowed disabled:opacity-60`;

  /** Mobil / Dock: Bottom-Sheet */
  const vehicleField = (
    <button
      id={isDocked ? "hb-vehicle-docked" : "hb-vehicle"}
      type="button"
      onClick={() => setVehiclePickerOpen(true)}
      disabled={options.length === 0}
      className={vehicleFieldCls}
      aria-haspopup="dialog"
      aria-expanded={vehiclePickerOpen}
      aria-label={t("heroBookingBar.selectVehicleAria")}
    >
      {vehicleFieldInner}
    </button>
  );

  /** Desktop: Dropdown direkt unter dem Feld */
  const vehicleDropdown = (
    <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <PopoverTrigger asChild>
        <button
          id="hb-vehicle-desktop"
          type="button"
          disabled={options.length === 0}
          className={vehicleFieldCls}
          aria-label={t("heroBookingBar.selectVehicleAria")}
        >
          {vehicleFieldInner}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(28rem,90vw)] p-1.5 z-[60]">
        <ul className="flex flex-col">
          <li>
            <button
              type="button"
              onClick={() => {
                setDropdownOpen(false);
                  handleVehiclePicked({
                  id: "__advice__",
                  dbName: "",
                  displayName: t("vehiclePicker.adviceTitle"),
                  displaySubtitle: t("vehiclePicker.adviceText"),
                    isAdvice: true,
                  }, true);
              }}
              className="w-full min-h-14 flex items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-secondary"
            >
              <span className="shrink-0 w-16 aspect-[4/3] rounded-md bg-accent/10 border border-accent/20 flex items-center justify-center">
                <HelpCircle className="h-7 w-7 text-accent" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">{t("vehiclePicker.adviceTitle")}</span>
                <span className="block text-xs text-muted-foreground">{t("vehiclePicker.adviceText")}</span>
              </span>
            </button>
          </li>
          {options.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => {
                  setDropdownOpen(false);
                  handleVehiclePicked(o, true);
                }}
                className={`w-full flex items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-secondary ${
                  o.id === vehicleId ? "bg-secondary" : ""
                }`}
                aria-pressed={o.id === vehicleId}
              >
                <span className="shrink-0 w-16 aspect-[4/3] rounded-md bg-secondary/60 border border-border overflow-hidden flex items-center justify-center">
                  <img
                    src={imageForVehicle(o.dbName, o.imageUrl)}
                    alt=""
                    aria-hidden
                    className="w-full h-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold truncate">{o.displayName}</span>
                  <span className="block text-xs text-muted-foreground truncate">{o.displaySubtitle}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );


  const pickupField = (
    <DateField
      id={isDocked ? "hb-pickup-docked" : "hb-pickup"}
      value={pickup}
      minDate={today}
      placeholder={t("heroBookingBar.pickupPlaceholder")}
      placeholderMobile={isDocked ? undefined : t("heroBookingBar.pickupLabel")}
      ariaLabel={t("heroBookingBar.pickupAria")}
      onSelect={(iso) => {
        setPickup(iso);
        if (returnD && iso && returnD < iso) setReturnD("");
      }}
    />
  );

  const returnField = (
    <DateField
      id={isDocked ? "hb-return-docked" : "hb-return"}
      value={returnD}
      minDate={parseIso(minReturn) ?? today}
      placeholder={t("heroBookingBar.returnPlaceholder")}
      placeholderMobile={isDocked ? undefined : t("heroBookingBar.returnLabel")}
      ariaLabel={t("heroBookingBar.returnAria")}
      onSelect={setReturnD}
    />
  );

  if (isDocked) {
    return (
      <>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-[1.6]">{vehicleField}</div>
          <div className="min-w-0 flex-1">{pickupField}</div>
          <button
            type="button"
            onClick={handleSubmit}
            className="group inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md text-sm font-bold whitespace-nowrap transition-all duration-200 gradient-primary text-primary-foreground"
          >
            {t("heroBookingBar.dockedCta")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </button>
          {/* Telefon & WhatsApp stehen im Header – hier keine Dopplung. */}

        </div>


        <VehiclePickerSheet
          open={vehiclePickerOpen}
          onOpenChange={setVehiclePickerOpen}
          options={options}
          selectedId={vehicleId}
          onPick={handleVehiclePicked}
        />

        {sheetVehicle && (
          <InquirySheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            vehicleType={sheetVehicle.vehicleType === "kleinbus" ? "kuehl" : sheetVehicle.vehicleType}
            vehicleLabel={sheetVehicle.fullLabel}
            vehicleId={sheetVehicle.id}
            vehicleImage={sheetVehicle.isAdvice ? undefined : imageForVehicle(sheetVehicle.dbName, sheetVehicle.imageUrl)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div ref={rootRef} className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_auto] gap-3 lg:gap-4 items-end">
          {/* Vehicle picker */}
          <div className="min-w-0">
            <label htmlFor="hb-vehicle" className={labelCls}>
              {t("heroBookingBar.vehicleLabel")}
            </label>
            <div className="lg:hidden">{vehicleField}</div>
            <div className="hidden lg:block">{vehicleDropdown}</div>
          </div>

          {/* Datumsfelder – mobil nebeneinander */}
          <div className="grid grid-cols-2 gap-3 lg:contents">
            <div className="min-w-0">
              <label htmlFor="hb-pickup" className={labelCls}>
                {t("heroBookingBar.pickupLabel")}
              </label>
              {pickupField}
            </div>

            <div className="min-w-0">
              <label htmlFor="hb-return" className={labelCls}>
                {t("heroBookingBar.returnLabel")}
              </label>
              {returnField}
            </div>
          </div>

          {/* CTA */}
          <div className="min-w-0">
            <span className={`${labelCls} invisible`} aria-hidden>
              .
            </span>
            <button
              type="button"
              onClick={handleHeroCta}
              className="group inline-flex items-center justify-center gap-2 h-12 lg:h-[52px] w-full lg:w-auto px-5 lg:px-6 rounded-[10px] text-sm font-bold whitespace-nowrap transition-all duration-200 bg-primary hover:bg-primary-hover text-primary-foreground"
            >
              {t("heroBookingBar.heroCta")}
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-1"
                strokeWidth={2}
              />
            </button>
          </div>
        </div>
      </div>




      {/* Vehicle picker sheet */}
      <VehiclePickerSheet
        open={vehiclePickerOpen}
        onOpenChange={setVehiclePickerOpen}
        options={options}
        selectedId={vehicleId}
        onPick={handleVehiclePicked}
      />

      {sheetVehicle && (
        <InquirySheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          vehicleType={sheetVehicle.vehicleType === "kleinbus" ? "kuehl" : sheetVehicle.vehicleType}
          vehicleLabel={sheetVehicle.fullLabel}
          vehicleId={sheetVehicle.id}
          vehicleImage={sheetVehicle.isAdvice ? undefined : imageForVehicle(sheetVehicle.dbName, sheetVehicle.imageUrl)}
        />
      )}
    </>
  );
};

export default HeroBookingBar;
